import classNames from 'classnames';
import parse from 'html-react-parser';
import { FC } from 'react';

import { ActionableLink, HUIImage } from 'root/widgets/common-components';

import { AgentBadge } from 'root/widgets/agent-badge';
import { HIVE_STATIC_VERSION } from 'root/widgets/constants';
import { EVENT_NAMES, emit } from 'root/widgets/events';
import { extractDataAutomationId, filterMetadata } from 'root/widgets/utils';

import './agent-info.scss';
import { AgentInfoProps } from './types';

const verifiedIcon = `https://cdn.pgimgs.com/hive-ui/static/${HIVE_STATIC_VERSION}/pg-icons/verified.svg`;
const certifiedIcon = `https://cdn.pgimgs.com/hive-ui/static/${HIVE_STATIC_VERSION}/pg-icons/certified.svg`;

const AgentInfo: FC<AgentInfoProps> = ({ agent, verification, rating, certificate, metadata, context }) => {
  const handleReviewClick = () => {
    emit(EVENT_NAMES.CONTACT_AGENT.REVIEW_CLICK, context);
    return rating?.onClick?.();
  };

  const shouldTruncateName = agent.description || rating || certificate?.isCertified;
  const profileLinkObject = agent.shouldShowProfile ? { href: agent.profileUrl, onClick: agent.onClick } : {};

  return (
    <div
      className={classNames('agent-info-root', { corporate: agent.isCorporate })}
      {...filterMetadata(metadata)}
      {...extractDataAutomationId(metadata)}
    >
      <div className="avatar-wrapper">
        <ActionableLink
          {...profileLinkObject}
          className={classNames('avatar-link', { 'no-link': !agent.shouldShowProfile })}
        >
          {agent.avatar ? (
            <HUIImage className="avatar" src={agent.avatar} alt={agent.name} />
          ) : (
            <i className="pgicon-agent" />
          )}
        </ActionableLink>
        {verification?.isVerified && (
          <AgentBadge
            className="verification"
            icon={verifiedIcon}
            text={verification.text}
            explanation={verification.explanation}
          />
        )}
      </div>
      <div className="details-wrapper">
        <div className="agent-name-wrapper">
          <ActionableLink
            {...profileLinkObject}
            className={classNames('agent-name', {
              'truncate-line': shouldTruncateName,
              'no-link': !agent.shouldShowProfile,
            })}
          >
            {agent.name}
          </ActionableLink>
        </div>
        {agent.description && (
          <div className="agent-description">
            {typeof agent.description === 'string' ? parse(agent.description) : agent.description}
          </div>
        )}
        {rating && (
          <ActionableLink
            className={classNames('agent-rating', { 'no-review': !rating.hasReview })}
            href={rating.href}
            onClick={handleReviewClick}
            target={rating.target || '_blank'}
          >
            <i className="pgicon-star" />
            {rating.score}
            {rating.text && <span className="rating-text">{rating.text}</span>}
            {rating.hasReview && rating.text && <i className="pgicon-arrow-right" />}
          </ActionableLink>
        )}
        {certificate?.isCertified && (
          <AgentBadge
            className="agent-certificate"
            icon={certifiedIcon}
            text={certificate.text}
            explanation={certificate.explanation}
          />
        )}
      </div>
    </div>
  );
};

AgentInfo.displayName = 'AgentInfo';

export default AgentInfo;
